import { useState, useMemo } from 'react';
import { Download } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { GettingStarted } from './GettingStarted';
import { EndpointCard } from './EndpointCard';
import { Button } from '@/components/ui/button';
import type { OpenAPISpec, EndpointInfo } from '@/types/openapi';
import { buildSaasSpec } from '@/lib/openapiExport';

export type AudienceFilter = 'saas' | 'all';

interface ApiDocsLayoutProps {
  spec: OpenAPISpec;
  endpointsByTag: Record<string, EndpointInfo[]>;
  audience: AudienceFilter;
  onAudienceChange: (audience: AudienceFilter) => void;
}

const audienceOptions: { value: AudienceFilter; label: string }[] = [
  { value: 'saas', label: 'SaaS integration' },
  { value: 'all', label: 'All endpoints' },
];

function downloadSaasSpec(spec: OpenAPISpec) {
  const json = JSON.stringify(buildSaasSpec(spec), null, 2);
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'portainer-bridge-saas-openapi.json';
  // Attached and revoked on the next tick: Safari and older Firefox start the
  // download asynchronously and need the anchor in the document.
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ApiDocsLayout({ spec, endpointsByTag, audience, onAudienceChange }: ApiDocsLayoutProps) {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointInfo | null>(null);
  const [selectedGettingStarted, setSelectedGettingStarted] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredEndpoints = useMemo(() => {
    if (!searchQuery) return endpointsByTag;

    const filtered: Record<string, EndpointInfo[]> = {};
    const query = searchQuery.toLowerCase();

    for (const [tag, endpoints] of Object.entries(endpointsByTag)) {
      const matches = endpoints.filter(
        (ep) =>
          ep.path.toLowerCase().includes(query) ||
          ep.operation.summary?.toLowerCase().includes(query) ||
          ep.operation.description?.toLowerCase().includes(query)
      );
      if (matches.length > 0) {
        filtered[tag] = matches;
      }
    }
    return filtered;
  }, [endpointsByTag, searchQuery]);

  // Auto-select Getting Started when spec loads (adjusted during render, not in an effect)
  if (!selectedEndpoint && !selectedGettingStarted && Object.keys(filteredEndpoints).length > 0) {
    setSelectedGettingStarted(true);
  }

  // Handle endpoint selection - deselect Getting Started when endpoint is selected
  const handleSelectEndpoint = (endpoint: EndpointInfo) => {
    setSelectedEndpoint(endpoint);
    setSelectedGettingStarted(false);
    setSelectedGroup(endpoint.operation.tags?.[0] || null);
  };

  // Handle Getting Started selection - deselect endpoint
  const handleSelectGettingStarted = () => {
    setSelectedGettingStarted(true);
    setSelectedEndpoint(null);
    setSelectedGroup(null);
  };

  // Switching to "SaaS integration" hides admin endpoints; leave an admin
  // endpoint that is open by falling back to Getting Started.
  const handleAudienceChange = (next: AudienceFilter) => {
    if (next === 'saas' && selectedEndpoint?.operation['x-audience'] === 'admin') {
      setSelectedEndpoint(null);
      setSelectedGroup(null);
      setSelectedGettingStarted(true);
    }
    onAudienceChange(next);
  };

  // Update selected endpoint if current selection no longer exists in filtered list
  // (adjusted during render; the replacement is in the filtered list, so this settles in one pass)
  if (selectedEndpoint && searchQuery) {
    const stillExists = Object.values(filteredEndpoints)
      .flat()
      .some(
        (ep) =>
          ep.path === selectedEndpoint.path && ep.method === selectedEndpoint.method
      );
    if (!stillExists && Object.keys(filteredEndpoints).length > 0) {
      const firstTag = Object.keys(filteredEndpoints)[0];
      if (filteredEndpoints[firstTag]?.length > 0) {
        setSelectedGroup(firstTag);
        setSelectedEndpoint(filteredEndpoints[firstTag][0]);
      }
    }
  }

  return (
    <div className="flex h-full gap-4">
      {/* Sidebar */}
      <aside className="w-[280px] shrink-0 overflow-auto border-r pr-4">
        <div className="mb-3 grid grid-cols-2 gap-1 rounded-md bg-muted p-1" role="group" aria-label="Endpoint audience">
          {audienceOptions.map((opt) => (
            <Button
              key={opt.value}
              size="sm"
              variant={audience === opt.value ? 'default' : 'ghost'}
              aria-pressed={audience === opt.value}
              onClick={() => handleAudienceChange(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mb-3 w-full"
          title="SaaS endpoints only"
          onClick={() => downloadSaasSpec(spec)}
        >
          <Download className="h-4 w-4" />
          Download openapi.json
        </Button>
        <Sidebar
          tags={spec.tags || []}
          endpointsByTag={filteredEndpoints}
          selectedGroup={selectedGroup}
          selectedEndpoint={selectedEndpoint}
          selectedGettingStarted={selectedGettingStarted}
          onSelectGroup={setSelectedGroup}
          onSelectEndpoint={handleSelectEndpoint}
          onSelectGettingStarted={handleSelectGettingStarted}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto space-y-4 pb-4">
        {selectedGettingStarted && <GettingStarted />}
        {selectedEndpoint && (
          <EndpointCard endpoint={selectedEndpoint} spec={spec} />
        )}
      </main>
    </div>
  );
}